import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { organizeTitles } from './organization';

export const API = process.env.REACT_APP_BACKEND_URL;
export const familyGet = async (path, token, signal) => {
  const response = await fetch(`${API}/api/dnaconnect/${path}?token=${encodeURIComponent(token)}`, { signal, cache: 'no-store' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Não foi possível carregar as informações financeiras.');
  return body;
};

export const useFamilyFinance = token => {
  const [state, setState] = useState({ alunos: [], titles: [], loading: true, errors: [], metadataWarning: false });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion(value => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setState(previous => ({ ...previous, loading: true, errors: [] }));
      try {
        const [family, metadata] = await Promise.all([
          familyGet('dashboard', token, controller.signal),
          familyGet('financeiro/titulos', token, controller.signal).catch(() => null),
        ]);
        const alunos = family.filhos || family.students || family.alunos || [];
        const results = await Promise.allSettled(alunos.map(aluno => familyGet(`financeiro/${encodeURIComponent(aluno.id)}/dashboard`, token, controller.signal)));
        if (controller.signal.aborted) return;
        const dashboards = results.filter(result => result.status === 'fulfilled').map(result => result.value);
        const errors = results.flatMap((result, i) => result.status === 'rejected'
          ? [`${alunos[i].name || alunos[i].nome}: ${result.reason.message}`] : []);
        setState({ alunos, titles: organizeTitles(dashboards, Array.isArray(metadata) ? metadata : []), loading: false, errors, metadataWarning: !Array.isArray(metadata) });
      } catch (error) {
        if (!controller.signal.aborted) setState({ alunos: [], titles: [], loading: false, errors: [error.message], metadataWarning: false });
      }
    };
    load();
    return () => controller.abort();
  }, [token, version]);

  // Mantém a atualização visual já existente durante a preparação de boletos.
  useEffect(() => {
    if (state.loading || !state.titles.some(t => !t.boleto_disponivel && t.boleto_status === 'preparando')) return;
    const timer = setTimeout(reload, 12000);
    return () => clearTimeout(timer);
  }, [state.loading, state.titles, reload]);

  return { ...state, reload };
};
/** Copia a linha digitável oficial do título (mesma regra no portal e no atalho da home). */
export const useCopyLine = token => {
  const [copying, setCopying] = useState(null);
  const copyLine = useCallback(async t => {
    setCopying(t.view.key);
    try {
      const path = t.dnapay_id ? `financeiro/boleto/${t.dnapay_id}/linha-digitavel` : `financeiro/titulo/${t.id}/boleto/linha-digitavel`;
      const response = await familyGet(path, token);
      if (!response.linha_digitavel) throw new Error('Linha digitável indisponível.');
      await navigator.clipboard.writeText(response.linha_digitavel);
      toast.success('Linha digitável copiada.');
    } catch (error) { toast.error(error.message || 'Não foi possível copiar a linha digitável.'); }
    finally { setCopying(null); }
  }, [token]);
  return { copying, copyLine };
};
